import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2, CheckCircle2, Clock } from 'lucide-react';
import MarketplaceShell from './MarketplaceShell';
import { MktButton } from './marketplaceUi';
import { getStudy, getStudyEta, type StudyEta } from './marketplaceApi';

const POLL_MS = 6000;

function formatRemaining(seconds: number): string {
  const mins = Math.ceil(seconds / 60);
  return mins <= 1 ? 'about a minute' : `about ${mins} minutes`;
}

/**
 * Shown once, immediately after a study is submitted/paid. Polls study status +
 * a dynamic ETA (#39); results are also emailed. User controls navigation.
 */
const ProcessingScreen: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [params] = useSearchParams();
  const stateId = (location.state as { studyId?: number } | null)?.studyId;
  const studyId = stateId ?? (params.get('study') ? Number(params.get('study')) : undefined);
  const [ready, setReady] = useState(false);
  // A run that outran the report window ('delayed') — surfaced as a distinct
  // terminal state so the user isn't left on an eternal spinner.
  const [delayed, setDelayed] = useState(false);
  const [eta, setEta] = useState<StudyEta | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (!studyId) { navigate('/marketplace/run', { replace: true }); return; }
    let active = true;
    const stopPolling = () => { if (intervalRef.current) { clearInterval(intervalRef.current); intervalRef.current = null; } };
    const poll = async () => {
      try {
        const s = await getStudy(studyId);
        // Paid chat returns here after Stripe — send the user into the live chat.
        if (active && s.use_case === 'chat') { stopPolling(); navigate(`/marketplace/studies/${studyId}/chat`, { replace: true }); return; }
        if (active && (s.status === 'report_ready' || s.report_status === 'ready')) { setReady(true); stopPolling(); return; }
        // Terminal non-success states — stop polling and tell the user rather than spin forever.
        if (active && (s.report_status === 'delayed' || s.status === 'failed' || s.report_status === 'failed')) { setDelayed(true); stopPolling(); return; }
      } catch { /* keep polling */ }
      // The ETA is computed server-side from the run start, so it keeps counting
      // down correctly even if the user reloads or comes back later.
      try { const e = await getStudyEta(studyId); if (active) setEta(e); } catch { /* ignore */ }
    };
    poll();
    intervalRef.current = setInterval(poll, POLL_MS);
    return () => { active = false; stopPolling(); };
  }, [studyId, navigate]);

  // Below the soft-landing threshold we stop showing an exact number so the
  // estimate never sits at "0 minutes" while the Twins are still finishing.
  const inSoftLanding = !!eta && eta.remaining_seconds <= eta.soft_landing_seconds;
  const showCountdown = !!eta && !ready && !inSoftLanding && eta.remaining_seconds > 0;

  return (
    <MarketplaceShell maxWidth="max-w-xl">
      <div className="flex flex-col items-center py-16 text-center" role="status" aria-live="polite">
        {ready ? (
          <>
            <CheckCircle2 className="h-12 w-12 text-primary" aria-hidden="true" />
            <h1 className="mt-4 text-[22px] font-medium text-[#111]">Your results are ready</h1>
            <p className="mt-2 text-[13px] text-[#6b6b6b]">We’ve also emailed you a copy. You can run another study any time.</p>
            {studyId && (
              <div className="mt-6">
                <MktButton onClick={() => navigate(`/marketplace/studies/${studyId}/results`)}>View your results →</MktButton>
              </div>
            )}
          </>
        ) : delayed ? (
          <>
            <Clock className="h-12 w-12 text-primary" aria-hidden="true" />
            <h1 className="mt-4 text-[22px] font-medium text-[#111]">This one’s taking a little longer</h1>
            <p className="mt-2 max-w-md text-[13px] leading-relaxed text-[#6b6b6b]">
              Your Twins are still finishing up. You don’t need to wait here — we’ll email your results as soon as they’re ready, and you’ll find them in your studies.
            </p>
          </>
        ) : (
          <>
            <Loader2 className="h-12 w-12 animate-spin text-primary" aria-hidden="true" />
            <h1 className="mt-4 text-[22px] font-medium text-[#111]">Your study is running</h1>
            {showCountdown ? (
              <p className="mt-2 max-w-md text-[13px] leading-relaxed text-[#6b6b6b]">
                Your Twins are responding now — <span className="font-medium text-[#111]">{formatRemaining(eta!.remaining_seconds)} remaining</span>.
                You can safely leave; we’ll email your results and they’ll be here when you’re back.
              </p>
            ) : (
              <p className="mt-2 max-w-md text-[13px] leading-relaxed text-[#6b6b6b]">
                {inSoftLanding
                  ? 'Almost done — just finishing up your Twins’ responses. We’ll email your results any moment now.'
                  : 'Your Twins are responding now. We’ll email your results shortly — you don’t need to wait on this page.'}
              </p>
            )}
          </>
        )}
        <div className="mt-8">
          <MktButton variant={ready ? 'secondary' : 'primary'} onClick={() => navigate('/marketplace/done')}>See what else you can do →</MktButton>
        </div>
      </div>
    </MarketplaceShell>
  );
};

export default ProcessingScreen;
